import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import useAdminStore from '../store/adminStore';
import { useTranslation } from 'react-i18next';
import { ServiceGroupCard, OverallHealthSummary } from './monitoring';

// Container group order for display
const GROUP_ORDER = [
  'RabbitMQ Cluster',
  'Chat Backend',
  'RAG Service',
  'Webhook Service',
  'AI Agents',
  'API Gateway',
  'MCP Server',
];

export default function ServiceMetrics() {
  const { getRealtimeMetrics } = useAdminStore();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'compact'
  const { t } = useTranslation(['common', 'admin']);

  // Load metrics
  const loadMetrics = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const data = await getRealtimeMetrics();
      setMetrics(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load metrics:', err);
      setError(t('monitoring.error', { ns: 'admin' }));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(() => loadMetrics(false), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group metrics by container_group
  const groupedMetrics = useMemo(() => {
    const groups = {};
    metrics.forEach((metric) => {
      const group = metric.container_group || 'Other Services';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(metric);
    });

    // Sort groups by predefined order
    const sortedGroups = {};
    GROUP_ORDER.forEach((groupName) => {
      if (groups[groupName]) {
        sortedGroups[groupName] = groups[groupName];
      }
    });

    // Add any remaining groups not in ORDER
    Object.keys(groups).forEach((groupName) => {
      if (!sortedGroups[groupName]) {
        sortedGroups[groupName] = groups[groupName];
      }
    });

    return sortedGroups;
  }, [metrics]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">
            {t('monitoring.loading', { ns: 'admin', defaultValue: 'Loading service metrics...' })}
          </Typography>
        </Box>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <IconButton color="inherit" size="small" onClick={() => loadMetrics(true)}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {t('monitoring.title', { ns: 'admin', defaultValue: 'System Health Monitor' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('monitoring.subtitle', { ns: 'admin', defaultValue: 'Real-time monitoring of all services grouped by container stack' })}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  bgcolor: alpha('#3b82f6', 0.1),
                  color: '#3b82f6',
                },
              },
            }}
          >
            <ToggleButton value="grouped">
              <Tooltip title="Grouped View">
                <GridViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="compact">
              <Tooltip title="Compact View">
                <ListViewIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Refresh now">
            <IconButton
              onClick={() => loadMetrics(true)}
              disabled={refreshing}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: alpha('#3b82f6', 0.1) },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Health Summary */}
      <OverallHealthSummary metrics={metrics} />

      {/* Service Groups */}
      <Box>
        {Object.entries(groupedMetrics).map(([groupName, services]) => (
          <ServiceGroupCard
            key={groupName}
            groupName={groupName}
            services={services}
            defaultExpanded={viewMode === 'grouped'}
          />
        ))}
      </Box>

      {/* Last updated info */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('monitoring.autoRefresh', { ns: 'admin', defaultValue: 'Auto-refreshes every 30 seconds' })} • {t('monitoring.lastUpdated', { ns: 'admin', defaultValue: 'Last updated' })}: {new Date().toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
}
